import { ReactNode } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { GROUP_PANEL_TASK_STATUSES, TaskGroupSummary, TaskStatuses } from '../../types';
import getGroupDisplay from '../../utils/getGroupDisplay';

import RightSidebarDrawer from 'modules/common/components/RightDrawer/Drawer';
import CloseXIcon from 'modules/common/components/Icons/CloseXIcon';
import useMemberTasks from 'services/memberTasks/queries/useMemberTasks';

interface TaskGroupDrawerProps {
    group: TaskGroupSummary;
    open: boolean;
    onClose: () => void;
    /** Body content below the meta block — the default expand list (GroupTaskList) or a custom flow. */
    children: ReactNode;
}

// The group_template.area is a TaskProductArea value (accounting/taxes/…, always present, worst
// case "other"); capitalized it becomes the header label, matching the Figma drill-in.
function areaLabel(area: string): string {
    return area.charAt(0).toUpperCase() + area.slice(1);
}

/**
 * Reusable group drawer shell: the responsive side panel, header (area + close), and the standard
 * meta block (due chip, title, description) derived from the group template. Custom group flows
 * (group_action.type === 'custom') reuse this and pass their own body as children — only the task
 * UI/actions differ. The default expand flow passes <GroupTaskList />.
 */
export default function TaskGroupDrawer({ group, open, onClose, children }: Readonly<TaskGroupDrawerProps>) {
    // display_name/description may carry {placeholder} tokens (e.g. {month}) — interpolate from context.
    const { title, description } = getGroupDisplay(group);

    // Reuses the same query the body renders (React Query dedupes the identical key) to know when
    // every task is done — at which point the panel offers a Done button to close + sync the feed.
    const { data: tasks } = useMemberTasks(
        { groupId: group.id, statuses: GROUP_PANEL_TASK_STATUSES },
        { enabled: open }
    );
    const allCompleted = !!tasks && tasks.length > 0 && tasks.every((task) => task.status === TaskStatuses.COMPLETED);

    return (
        <RightSidebarDrawer
            open={open}
            onClose={onClose}
            ariaLabel={title}
            hideBackdrop={false}
            transitionDuration={400}
            // Sit just below the modal layer so a task's submit-action Dialog (opened from a row)
            // renders above the drawer and stays interactive. RightSidebarDrawer otherwise forces
            // modal + 1, which would trap the Dialog behind the panel.
            sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}>
            <Stack sx={{ height: '100%', backgroundColor: 'background.paper', borderRadius: { md: 3 } }}>
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        flexShrink: 0,
                        height: 48,
                        borderBottom: 1,
                        borderColor: 'grey.100',
                    }}>
                    <Typography variant="h5">{areaLabel(group.group_template.area)}</Typography>
                    <IconButton
                        aria-label="Close"
                        onClick={onClose}
                        sx={{ bgcolor: 'cement.100', width: 24, height: 24, '& .MuiSvgIcon-root': { fontSize: 12 } }}>
                        <CloseXIcon />
                    </IconButton>
                </Stack>
                {open && (
                    // minHeight: 0 lets this flex child shrink below its content height so overflowY
                    // actually scrolls (default min-height:auto would grow past the panel and clip).
                    <Stack sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pt: 3, px: 3, pb: 2, gap: 6 }}>
                        <Stack sx={{ gap: 2 }}>
                            <Stack sx={{ gap: 2, alignItems: 'flex-start' }}>
                                {group.next_due_date && (
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            bgcolor: 'cement.100',
                                            borderRadius: 1.5,
                                            px: 1,
                                            py: 0.5,
                                        }}>
                                        <Typography variant="overline" color="cement.700">
                                            {`DUE ${dayjs(group.next_due_date).format('M/DD')}`}
                                        </Typography>
                                    </Box>
                                )}
                                <Typography variant="h3">{title}</Typography>
                            </Stack>
                            {description && (
                                <Typography variant="body1" color="grey.800">
                                    {description}
                                </Typography>
                            )}
                        </Stack>
                        {children}
                    </Stack>
                )}
                {open && allCompleted && (
                    <Stack sx={{ flexShrink: 0, px: 3, pt: 2, pb: 3 }}>
                        <Button variant="contained" color="primary" fullWidth onClick={onClose}>
                            Done
                        </Button>
                    </Stack>
                )}
            </Stack>
        </RightSidebarDrawer>
    );
}
